<template>
  <div>
    <b-select
      v-model="form.fieldDefinitionId"
      :error-messages="getErrorMessages(v$.fieldDefinitionId)"
      :items="availableFieldDefinitions"
      :loading="$loading.isLoading('fieldDefinitions')"
      item-title="name"
      item-value="id"
      label="Поле"
      placeholder="Оберіть поле з бібліотеки"
      @blur="v$.fieldDefinitionId.$touch()"
      @focus="$directory.fill('fieldDefinitions')" />

    <b-input
      :model-value="selectedFieldType || '---'"
      disabled
      label="Тип поля"
      placeholder="Оберіть поле для відображення типу" />

    <b-select
      v-if="hasEntitySource"
      v-model="form.entitySource"
      :error-messages="getErrorMessages(v$.entitySource)"
      :items="$directory.get('docTypeFieldEntitySources')"
      :loading="$loading.isLoading('docTypeFieldEntitySources')"
      clearable
      label="Сутність"
      placeholder="Оберіть сутність"
      @blur="v$.entitySource.$touch()"
      @focus="$directory.fill('docTypeFieldEntitySources')" />

    <v-checkbox
      v-if="selectedFieldType === 'boolean'"
      v-model="form.defaultValue"
      hide-details
      label="Значення за замовчуванням" />
    <b-input-number
      v-else-if="selectedFieldType === 'integer'"
      v-model="form.defaultValue"
      label="Значення за замовчуванням" />
    <b-input-number
      v-else-if="selectedFieldType === 'decimal'"
      v-model="form.defaultValue"
      :step="0.01"
      label="Значення за замовчуванням" />
    <b-input-date
      v-else-if="selectedFieldType === 'date'"
      v-model="form.defaultValue"
      label="Значення за замовчуванням" />
    <v-textarea
      v-else-if="selectedFieldType === 'text'"
      v-model="form.defaultValue"
      density="compact"
      label="Значення за замовчуванням"
      rows="3"
      variant="outlined" />
    <b-input
      v-else
      v-model="form.defaultValue"
      :disabled="!selectedFieldType"
      label="Значення за замовчуванням"
      placeholder="Введіть значення" />

    <div class="font-weight-bold font-size-15 mt-4 mb-2">Налаштування поля</div>

    <v-checkbox
      v-model="form.isRequired"
      hide-details
      label="Обов'язкове для заповнення" />
    <v-checkbox
      :model-value="!form.isHidden"
      hide-details
      label="Показувати в картці документа"
      @update:model-value="form.isHidden = !$event" />
    <v-checkbox
      v-model="form.isReadonly"
      hide-details
      label="Тільки перегляд" />
  </div>
</template>

<script setup lang="ts">
import { Dialog } from '@/dialog'
import { fillDirectory, getDirectory } from '@/directory'
import {
  DocumentTypeField,
  DocumentTypeFieldCredentials,
  DocumentTypeFieldDefinition,
} from '@/utils/types/documentTypeField'
import { docTypeFieldAdd, docTypeFieldUpdate } from '@/requests/documentType'
import { computed, reactive, watch } from 'vue'
import { getErrorMessages } from 'best-modules/utils'
import { required, requiredIf } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import { BInputDate, BInputNumber } from 'best-modules/components'

defineOptions({
  name: 'DocTypeField',
})

const props = defineProps<{
  dialog: Dialog
}>()

type DocTypeFieldDialogData = {
  docTypeId: number
  existFieldIds: number[]
  field?: DocumentTypeField
}

type DocTypeFieldForm = Omit<
  DocumentTypeFieldCredentials,
  'fieldDefinitionId' | 'defaultValueJson'
> & {
  fieldDefinitionId: number | null
  defaultValue: any
}

const dialogData = props.dialog.dialogData as DocTypeFieldDialogData
const sourceField = dialogData.field

fillDirectory('fieldDefinitions')
fillDirectory('docTypeFieldEntitySources')

const form = reactive<DocTypeFieldForm>({
  fieldDefinitionId: sourceField?.fieldDefinitionId ?? null,
  label: null,
  sortOrder: sourceField?.sortOrder || dialogData.existFieldIds.length,
  isRequired: sourceField?.isRequired ?? false,
  isReadonly: sourceField?.isReadonly ?? false,
  isHidden: sourceField?.isHidden ?? false,
  defaultValue: sourceField?.defaultValueJson?.value ?? null,
  optionsJson: null,
  entitySource: sourceField?.entitySource ?? null,
  renderSize: sourceField?.renderSize ?? 'small',
})

const availableFieldDefinitions = computed(() => {
  return getDirectory('fieldDefinitions').filter(
    (fieldDefinition: DocumentTypeFieldDefinition) => {
      return (
        fieldDefinition.id === form.fieldDefinitionId ||
        !dialogData.existFieldIds.includes(fieldDefinition.id)
      )
    }
  )
})

const selectedFieldDefinition = computed(() => {
  return getDirectory('fieldDefinitions').find(
    (fieldDefinition: DocumentTypeFieldDefinition) =>
      fieldDefinition.id === form.fieldDefinitionId
  )
})

const selectedFieldType = computed(() => {
  return selectedFieldDefinition.value?.fieldType ?? null
})

const hasEntitySource = computed(() => {
  return ['select', 'multiselect'].includes(selectedFieldType.value)
})

watch(
  () => form.fieldDefinitionId,
  () => {
    form.defaultValue = selectedFieldType.value === 'boolean' ? false : null
    form.entitySource = hasEntitySource.value ? form.entitySource : null
  }
)

const rules = {
  fieldDefinitionId: { required },
  entitySource: {
    required: requiredIf(() => hasEntitySource.value),
  },
}

const v$ = useVuelidate(rules, form)

const buildPayload = (): DocumentTypeFieldCredentials => {
  if (form.fieldDefinitionId === null) {
    throw new Error('Field definition is required')
  }

  return {
    fieldDefinitionId: form.fieldDefinitionId,
    label: null,
    sortOrder: sourceField?.sortOrder || dialogData.existFieldIds.length,
    isRequired: form.isRequired,
    isReadonly: form.isReadonly,
    isHidden: form.isHidden,
    defaultValueJson:
      form.defaultValue === null || form.defaultValue === ''
        ? null
        : { value: form.defaultValue },
    optionsJson: null,
    entitySource: hasEntitySource.value ? form.entitySource : null,
    renderSize: form.renderSize,
  }
}

const submit = () => {
  const payload = buildPayload()

  if (props.dialog.action === 'update') {
    if (!sourceField) {
      throw new Error('Field is required for update action')
    }

    return docTypeFieldUpdate(dialogData.docTypeId, sourceField.id, payload)
  }

  return docTypeFieldAdd(dialogData.docTypeId, payload)
}

defineExpose({
  v$,
  submit,
})
</script>
